import React, { useState } from 'react';

import { useField } from 'formik';

import { Button, Text } from '@shared/components/bootstrap';
import { FieldFormGroupProps } from '@shared/components/fields/field_form_group';
import { FormikFieldFormGroup } from '@shared/components/fields/formik/formik_field_form_group';
import { Spacer } from '@shared/components/helpers';

import { FileFormControl } from '../file_form_control';

type Attachment = {
  id: string;
  filename: string;
  signedID: string;
  url: string;
};

type InputFormGroupProps = FieldFormGroupProps & {
  name: string;
  accept: string;
  initial?: Attachment[];
};

type Upload = {
  file: File;
  signedID: string;
};

export const FormikFilesFormGroup: React.FC<InputFormGroupProps> = ({ accept, name, initial = [], ...props }) => {
  const [field, _, helpers] = useField<string[]>(name);
  const [records, setRecords] = useState<Array<Attachment | Upload>>(() => initial);

  const onUpload = (file: File, signedID: string) => {
    setRecords((prev) => [...prev, { file, signedID }]);
    helpers.setValue([...(field.value || []), signedID]);
  };

  const onRemove = (record: Upload | Attachment) => {
    setRecords((prev) => prev.filter((f) => f.signedID !== record.signedID));
    helpers.setValue((field.value ?? []).filter((id) => id !== record.signedID));
  };

  const onMove = (index: number, direction: 'up' | 'down') => {
    setRecords((prev) => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      helpers.setValue(next.map((r) => r.signedID));
      return next;
    });
  };

  return (
    <FormikFieldFormGroup name={name} {...props}>
      <div>
        {records.map((record, index) => (
          <div key={record.signedID} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            {records.length > 1 && (
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
                <Button
                  kind="default"
                  size="sm"
                  onClick={() => onMove(index, 'up')}
                  disabled={index === 0}
                  aria-label={`Move item ${index + 1} up`}
                  title="Move up"
                  style={{ padding: '0 4px', lineHeight: 1 }}
                >
                  &#9650;
                </Button>
                <Button
                  kind="default"
                  size="sm"
                  onClick={() => onMove(index, 'down')}
                  disabled={index === records.length - 1}
                  aria-label={`Move item ${index + 1} down`}
                  title="Move down"
                  style={{ padding: '0 4px', lineHeight: 1 }}
                >
                  &#9660;
                </Button>
              </span>
            )}
            <Text wrapping="truncate">
              {'filename' in record ? (
                <a href={record.url} target="_blank" rel="noopener noreferrer">
                  {record.filename}
                </a>
              ) : (
                record.file.name
              )}
            </Text>
            <Button kind="danger" size="sm" onClick={() => onRemove(record)}>
              Remove
            </Button>
          </div>
        ))}
        <Spacer height="8px" />
        <FileFormControl id={name} accept={accept} onUpload={onUpload} multiple label="Add File" />
      </div>
    </FormikFieldFormGroup>
  );
};
